import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, inject, Injectable } from '@angular/core';
import {
  AuthConfig,
  ForgotPasswordResponse,
  LoginResponseData,
} from './models';
import { catchError, map, Observable, tap, throwError } from 'rxjs';
import { CookiesService, PermissionsService, SessionService } from '@shared';
import { AUTH_CONFIG } from './config.token';
import { ApiResponse } from '../../../shared/src/lib/models/securepay';
import { Router } from '@angular/router';

const PERSISTED_AUTH_KEYS: ReadonlySet<string> = new Set([
  'token',
  'refreshToken',
  'tokenType',
  'userId',
  'data',
  'description',
  'merchantRegistrationId',
  'accessToken',
  'kycStatus',
]);

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly cookieStorage = inject(CookiesService);
  private readonly router = inject(Router);
  private readonly session = inject(SessionService);
  private readonly permissions = inject(PermissionsService);

  private get headers(): HttpHeaders {
    let headers = new HttpHeaders();
    const accessToken = this.cookieStorage.get(this.cookieStorage.COOKIE_NAME);
    const tokenType = this.cookieStorage.get('tokenType');

    const sanitizedTokenType = tokenType?.replace(/[\r\n]/g, '') || '';
    const sanitizedAccessToken = accessToken?.replace(/[\r\n]/g, '') || '';

    headers = headers.append(
      'Authorization',
      `${sanitizedTokenType} ${sanitizedAccessToken}`,
    );

    return headers;
  }

  constructor(@Inject(AUTH_CONFIG) private config: AuthConfig) {}

  getUserId() {
    return this.cookieStorage.get('userId');
  }

  getUserData() {
    return this.cookieStorage.get('data');
  }

  login(payload: {
    EmailAddress: string;
    Password: string;
  }): Observable<LoginResponseData> {
    const encodedData = btoa(JSON.stringify(payload));
    let headers = this.headers;
    headers = headers.append('Basic', encodedData);

    return this.http
      .post<
        ApiResponse<LoginResponseData>
      >(`${this.config.baseApi}/api/Auth/login`, {}, { headers })
      .pipe(
        map((res) => {
          if (res.success) {
            if (!res.data?.userId) {
              throw new Error(res.message || 'Login failed');
            }
            this.setUserDetails(res.data);
          } else {
            throw new Error(res.data?.description || 'Login failed');
          }
          return res.data as LoginResponseData;
        }),
        catchError((err) => {
          return throwError(() => new Error(err.message || 'Login Failed'));
        }),
      );
  }
  setUserDetails(data: Readonly<Record<string, any>>) {
    if (!data) return;
    for (const key of Object.keys(data)) {
      if (!PERSISTED_AUTH_KEYS.has(key)) continue;
      const value = data[key];
      if (value === undefined || value === null) continue;
      if (typeof value === 'object') {
        this.cookieStorage.set(key, JSON.stringify(value));
      } else {
        this.cookieStorage.set(key, String(value));
      }
    }
  }

  logout(): Observable<void> {
    return this.http
      .post<void>(
        `${this.config.baseApi}/api/Auth/logout`,
        {},
        { headers: this.headers },
      )
      .pipe(
        tap(() => this.finalizeLogout()),
        catchError((err) => {
          this.finalizeLogout();
          return throwError(() => err);
        }),
      );
  }

  private finalizeLogout(): void {
    // The single choke point for every logout path in both apps.
    this.session.stop();
    this.permissions.clearPermissions();
    this.cookieStorage.clearAll();
    this.router.navigate(['/auth/login']);
  }

  verifyEmail(payload: { token: string; userId: string }) {
    const headers: HttpHeaders = this.headers;
    return this.http
      .post<{
        userId?: string;
        description?: string;
      }>(`${this.config.baseApi}/api/Auth/auth/Confirm-Email`, payload, {
        headers,
      })
      .pipe(
        map((res) => {
          if (!res?.userId) throw new Error(res?.description || 'Failed');
          return true;
        }),
        catchError((err) =>
          throwError(
            () => new Error(err?.message || err?.description || 'Failed'),
          ),
        ),
      );
  }

  sendOTP(otpType: number) {
    const headers: HttpHeaders = this.headers;
    const userId = this.cookieStorage.get('userId');
    if (!userId) return throwError(() => new Error('No userId'));
    const payload = { OtpType: otpType, userId };

    return this.http
      .post<
        ApiResponse<{ userId?: string; description: string }>
      >(`${this.config.baseApi}/api/Auth/send-otp`, payload, { headers })
      .pipe(
        map((res) => {
          if (res.success) {
            if (!res?.data?.userId) throw new Error(res?.message || 'Failed');
            return true;
          } else {
            throw new Error(res?.data?.description || 'Failed');
          }
        }),
        catchError((err) =>
          throwError(() => new Error(err?.message || 'Failed')),
        ),
      );
  }

  validateOTP(token: string): Observable<LoginResponseData> {
    const userId = this.cookieStorage.get('userId');
    if (!userId) return throwError(() => new Error('No userId'));
    const headers: HttpHeaders = this.headers;
    const payload = { token, userId };

    return this.http
      .post<ApiResponse<LoginResponseData>>(
        `${this.config.baseApi}/api/Auth/validate-otp`,
        payload,
        {
          headers,
        },
      )
      .pipe(
        map((res) => {
          if (res.success) {
            if (!res.data?.token)
              throw new Error(res.data.description || res.message || 'Failed');
            this.setUserDetails(res.data);
          } else {
            const message = res.data ? res.data?.description : res.message;
            throw new Error(message || 'Verification Failed');
          }
          return res.data as LoginResponseData;
        }),
        catchError((err) =>
          throwError(() => new Error(err.message || 'Verification Failed')),
        ),
      );
  }

  forgotPassword(emailAddress: string) {
    const headers: HttpHeaders = this.headers;
    const payload = { emailAddress };

    return this.http
      .post<
        ApiResponse<{ data: ForgotPasswordResponse }>
      >(`${this.config.baseApi}/api/Auth/forgot-password`, payload, { headers })
      .pipe(
        map((res) => {
          if (!res?.success) throw new Error(res?.message || 'Failed');
          try {
            this.setUserDetails(res.data.data);
          } catch (error) {
            console.error('Error setting user details:', error);
          }
          return res;
        }),
        catchError((err) =>
          throwError(() => new Error(err?.message || 'Failed')),
        ),
      );
  }

  resetPassword(payload: {
    password: string;
    confirmPassword: string;
    userId: string;
  }): Observable<ApiResponse<{ description: string; [key: string]: any }>> {
    const headers: HttpHeaders = this.headers;

    return this.http
      .post<
        ApiResponse<{ description: string; [key: string]: any }>
      >(`${this.config.baseApi}/api/Auth/reset-password`, payload, { headers })
      .pipe(
        map((res) => {
          if (res.success) {
            return res;
          } else {
            const message = res.data ? res.data?.description : res.message;
            throw new Error(message || 'Password reset failed');
          }
        }),
        catchError((err) =>
          throwError(
            () =>
              new Error(
                err?.message || err?.description || 'Password reset failed',
              ),
          ),
        ),
      );
  }

  changePassword(payload: {
    userId: string;
    currentPassword: string;
    newPassword: string;
    confirmNewPassword: string;
  }): Observable<ApiResponse<unknown>> {
    const headers: HttpHeaders = this.headers;

    return this.http
      .post<
        ApiResponse<unknown>
      >(`${this.config.baseApi}/api/Auth/change-password`, payload, { headers })
      .pipe(
        map((res) => {
          if (res.success) {
            return res;
          }
          throw new Error(
            this.extractValidationMessage(res.message, res.data) ||
              'Failed to change password',
          );
        }),
        catchError((err) =>
          throwError(
            () =>
              new Error(
                this.extractValidationMessage(
                  err?.error?.message,
                  err?.error?.data,
                ) ||
                  err?.message ||
                  'Failed to change password',
              ),
          ),
        ),
      );
  }

  private extractValidationMessage(
    message: string | undefined,
    data: unknown,
  ): string | undefined {
    const descriptions = Array.isArray(data)
      ? data
          .map((item) => (item as { description?: string })?.description)
          .filter((description): description is string => !!description)
      : [];

    if (!descriptions.length) {
      return message;
    }

    return message
      ? `${message}: ${descriptions.join(' ')}`
      : descriptions.join(' ');
  }
}
